// src/pages/DHLSendSign.jsx
import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, TextField, Button, Typography } from '@mui/material';
import easyLogo from '../assets/images/easy_logo.png';
import dhlLogo from '../assets/images/dhllogo.png';
import Services from 'src/services/Services';
import { toast } from 'react-toastify';

const DHLSendSign = () => {
    const { barcode } = useParams();
    const [inputValue, setInputValue] = useState(barcode || '');
    const [token, setToken] = useState(null);
    const canvasWrapperRef = useRef(null);
    const canvasRef = useRef(null);

    // Canvas’ın CSS boyutuna göre çözünürlüğünü ayarlamak için state
    const [canvasSize, setCanvasSize] = useState({ width: 500, height: 300 });

    // Token’ı al
    useEffect(() => {
        Services.GetToken()
            .then((res) => {
                if (res.data.status) {
                    setToken(res.data.result.token);
                } else {
                    console.error('Token alınamadı:', res.data);
                }
            })
            .catch((err) => {
                console.error('Token isteği hatası:', err);
            });
    }, []);

    // URL’den barkod geldiğinde input’u doldur
    useEffect(() => {
        if (barcode) {
            setInputValue(barcode);
        }
    }, [barcode]);

    // Canvas boyutunu wrapper'ın genişliğine göre güncelle
    useEffect(() => {
        const updateCanvasSize = () => {
            if (canvasWrapperRef.current) {
                const wrapperWidth = canvasWrapperRef.current.getBoundingClientRect().width;
                const newWidth = Math.min(wrapperWidth, 500);
                const aspectRatio = 166 / 519; // yükseklik / en
                const newHeight = newWidth * aspectRatio;
                setCanvasSize({ width: newWidth, height: newHeight });
            }
        };

        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);
        return () => window.removeEventListener('resize', updateCanvasSize);
    }, []);

    // Çizim eventlerini ekle
    const [isDrawing, setIsDrawing] = useState(false);
    const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;

        const handleMouseDown = (e) => {
            const rect = canvas.getBoundingClientRect();
            setIsDrawing(true);
            setLastPos({
                x: (e.clientX - rect.left) * (canvas.width / rect.width),
                y: (e.clientY - rect.top) * (canvas.height / rect.height),
            });
        };

        const handleMouseMove = (e) => {
            if (!isDrawing) return;
            const rect = canvas.getBoundingClientRect();
            const x = (e.clientX - rect.left) * (canvas.width / rect.width);
            const y = (e.clientY - rect.top) * (canvas.height / rect.height);
            ctx.beginPath();
            ctx.moveTo(lastPos.x, lastPos.y);
            ctx.lineTo(x, y);
            ctx.stroke();
            setLastPos({ x, y });
        };

        const handleMouseUp = () => {
            setIsDrawing(false);
        };

        canvas.addEventListener('mousedown', handleMouseDown);
        canvas.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            canvas.removeEventListener('mousedown', handleMouseDown);
            canvas.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDrawing, lastPos, canvasSize]);

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    // --- Burada istediğin fonksiyon ---
    const sendDHLSign = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
            toast.error(
                'İmza alanı bulunamadı.'
            );
            return;
        }

        if (!inputValue) {
            toast.error(
                'Lütfen önce barkodu girin.'
            );
            return;
        }

        if (!token) {
            toast.error(
                'Token henüz yüklenmedi. Lütfen biraz bekleyip tekrar deneyin.'
            );
            return;
        }

        // Canvas'tan PNG formatında base64 string al
        const dataURL = canvas.toDataURL('image/png');
        // "data:image/png;base64,..." kısmını kırpıp sadece base64 veriyi alıyoruz
        const base64Signature = dataURL.replace(/^data:image\/png;base64,/, '');

        // Service çağrısı: token, barkod ve base64 imza verisi
        Services.DHLSendSign(token, inputValue, base64Signature)
            .then((res) => {
                if (res.data.status) {
                    toast.success('İmza başarıyla gönderildi.');
                    clearCanvas();
                } else {
                    toast.error(
                        `Gönderme hatası: ${res.data.message || 'Bilinmeyen hata'}`
                    );
                    console.error('Hata detayı:', res.data);
                }
            })
            .catch((err) => {
                toast.error(
                    'İmza gönderilirken bir hata oluştu.'
                );
                console.error('Servis çağrısı hatası:', err);
            });
    };
    // --- Fonksiyon sonu ---

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#FFCC00',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
            }}
        >
            {/* Üstte logolar */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                    width: '100%',
                }}
            >
                <Box
                    component="img"
                    src={easyLogo}
                    alt="Easy Logo"
                    sx={{
                        width: { xs: 80, sm: 120 },
                        height: { xs: 40, sm: 60 },
                        objectFit: 'contain',
                        mr: 1,
                    }}
                />
                <Box
                    component="img"
                    src={dhlLogo}
                    alt="DHL Logo"
                    sx={{
                        width: { xs: 80, sm: 120 },
                        height: { xs: 40, sm: 60 },
                        objectFit: 'contain',
                        ml: 1,
                    }}
                />
            </Box>

            {/* Barkod input */}
            <TextField
                label="Barkod"
                variant="outlined"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={!!barcode}
                sx={{
                    mb: 2,
                    width: '90%',
                    maxWidth: 400,
                    backgroundColor: '#FFFFFF',
                }}
            />

            {/* İmza başlığı */}
            <Typography variant="h6" sx={{ mb: 1 }}>
                İmza:
            </Typography>

            {/* Canvas wrapper: genişlik %90 viewport, max 500px */}
            <Box
                ref={canvasWrapperRef}
                sx={{
                    width: '90%',
                    maxWidth: 500,
                    backgroundColor: '#FFFFFF',
                    border: '2px solid #000',
                    cursor: 'crosshair',
                }}
            >
                <canvas
                    ref={canvasRef}
                    width={canvasSize.width}
                    height={canvasSize.height}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                    }}
                />
            </Box>

            {/* Butonlar */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                    variant="outlined"
                    onClick={clearCanvas}
                    sx={{ flex: '1 1 150px', maxWidth: 150, backgroundColor: '#fff' }}
                >
                    Temizle
                </Button>
                <Button
                    variant="outlined"
                    onClick={sendDHLSign}
                    sx={{ flex: '1 1 150px', maxWidth: 150, backgroundColor: '#fff' }}
                >
                    Teslim aldım. Onaylıyorum.
                </Button>
            </Box>
        </Box>
    );
};

export default DHLSendSign;
